import { useState } from "react";
import { useTheme } from "@mui/material";
import Button from "@components/Button/Button";
import LoadingOverlay from "@components/LoadingOverlay/LoadingOverlay";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import AttachmentsComponent from "@features/vessel-call/vessel-call-status/components/AttachmentComponent/AttachmentsComponent";
import ResolveDisputeButton from "@features/vessel-call/vessel-call-status/Operations/flow/ResolveDisputeButton";
import { formatAssociatedMovement } from "@features/vessel-call/vessel-call-status/Operations/grid/OperationsDisputes.mapper";
import { formatDateTime } from "@features/vessel-call/vessel-call-status/utils";
import { useDownloadCertificate } from "@pages/vessel-management/vessel-status/hooks/useDownloadCertificate";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { DISPUTE_HISTORY_TYPES } from "@services/disputes/types";
import { getDisputeById } from "@services/disputesApi";
import { useQuery } from "@tanstack/react-query";
import { translateNormalizedRole } from "@utils/roles";
import { checkPermission } from "@utils/user-permissions";
import { useTranslation } from "react-i18next";

type DisputeDetailsDrawerProps = {
  disputeId: string | null;
  isOpen: boolean;
  isCurrentLocationFOAA?: boolean;
  onClose: () => void;
  onChangeToNoDispute?: () => void;
  onUpdateDispute?: () => void;
  onResolveDispute?: () => void;
  onAcceptResolution?: () => void;
  onRejectResolution?: () => void;
};

const LANG_BASE = "vessel-call.operations";
const TEST_ID_BASE = "operations-dispute-details";
const SERVICE = "bff-vessel-call";

const getHistoryFieldsLabel = (historyType: string): { details: string; documents: string } => {
  switch (historyType) {
    case DISPUTE_HISTORY_TYPES.NO_DISPUTE:
      return {
        details: "no-dispute-comments",
        documents: "no-dispute-document",
      };
    case DISPUTE_HISTORY_TYPES.RESOLUTION_REQUEST:
      return {
        details: "resolution-details",
        documents: "resolution-documents",
      };
    case DISPUTE_HISTORY_TYPES.RESOLUTION_ACCEPTED:
      return {
        details: "accepted-resolution-comments",
        documents: "accepted-resolution-documents",
      };
    case DISPUTE_HISTORY_TYPES.RESOLUTION_REJECTED:
      return {
        details: "rejected-resolution-reason",
        documents: "rejected-resolution-documents",
      };
    default:
      return {
        details: "dispute-reason",
        documents: "dispute-documents",
      };
  }
};

export const RESOLVE_DISPUTE_RESOLUTION_PERMISSION = "canRequestDisputeResolution";

const DisputeDetailsDrawer = ({
  disputeId,
  isOpen,
  onClose,
  onChangeToNoDispute,
  onUpdateDispute,
  onResolveDispute,
  onAcceptResolution,
  onRejectResolution,
  isCurrentLocationFOAA = true,
}: DisputeDetailsDrawerProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { handleDownload, handlePreview } = useDownloadCertificate();
  const { userActionPermissions } = useAuthStore();
  const canRequestDispute = checkPermission(userActionPermissions, "canRequestDispute");
  const [validationErrorFOAA, setValidationErrorFOAA] = useState(false);

  const {
    data: dispute,
    isError,
    isLoading,
  } = useQuery({
    queryKey: ["dispute", SERVICE, disputeId],
    queryFn: () => getDisputeById(SERVICE, disputeId ?? ""),
    enabled: isOpen && Boolean(disputeId),
  });
  const isNoDisputeRecord = dispute?.status === "NO_DISPUTE";
  const isDisputeRaised = dispute?.status === "DISPUTE_RAISED";
  const isResolutionPending = dispute?.status === "RESOLUTION_PENDING";
  const historyEntries =
    dispute && dispute.history.length > 0
      ? dispute.history
      : dispute
        ? [
            {
              type: isNoDisputeRecord
                ? DISPUTE_HISTORY_TYPES.NO_DISPUTE
                : DISPUTE_HISTORY_TYPES.DISPUTE,
              createdBy: dispute.createdBy,
              createdByRole: dispute.createdByRole,
              createdAt: dispute.createdAt,
              disputeDetails: dispute.disputeDetails,
              documents: dispute.documents,
            },
          ]
        : [];

  const handleClose = () => {
    setValidationErrorFOAA(false);
    onClose();
  };

  const handleRequestResolution = () => {
    if (!isCurrentLocationFOAA) {
      setValidationErrorFOAA(true);
      return;
    }

    onResolveDispute?.();
  };

  const canResolveDispute = checkPermission(
    userActionPermissions,
    RESOLVE_DISPUTE_RESOLUTION_PERMISSION,
  );

  const dividerBorderTop = `1px solid ${theme.palette.strokeColor?.secondary}`;

  const renderDivider = (testId: string) => (
    <div data-testid={testId} className="my-6 w-full" style={{ borderTop: dividerBorderTop }} />
  );

  const reviewFooter =
    isResolutionPending && canRequestDispute ? (
      <div className="flex gap-3 px-6 py-6" data-testid={`${TEST_ID_BASE}-review-actions`}>
        <Button
          fullWidth
          label={<span className="normal-case font-medium">{t("common.cancel")}</span>}
          variant="text"
          onClick={handleClose}
          testId={`${TEST_ID_BASE}-review-cancel`}
        />
        <Button
          fullWidth
          label={<span className="normal-case font-medium">{t(`${LANG_BASE}.reject`)}</span>}
          variant="outlined"
          onClick={onRejectResolution}
          testId={`${TEST_ID_BASE}-reject-resolution`}
        />
        <Button
          fullWidth
          label={<span className="normal-case font-medium">{t(`${LANG_BASE}.accept`)}</span>}
          variant="contained"
          onClick={onAcceptResolution}
          testId={`${TEST_ID_BASE}-accept-resolution`}
        />
      </div>
    ) : undefined;

  const raisedDisputeFooter =
    !isDisputeRaised ||
    (!canRequestDispute && !canResolveDispute) ? undefined : canResolveDispute ? (
      <div className="flex w-[400px] flex-col px-6 py-6 border-t border-gray-200">
        {validationErrorFOAA && (
          <div
            className="min-w-0 break-words text-sm text-red-600 pb-4"
            data-testid={`${TEST_ID_BASE}-foaa-validation-error`}
            role="alert"
          >
            {t(`${LANG_BASE}.movement-to-foaa-required`)}
          </div>
        )}
        <div className="flex">
          <Button
            fullWidth
            label={<span className="normal-case font-medium">{t("common.cancel")}</span>}
            variant="text"
            onClick={handleClose}
            testId="side-drawer-close-button"
          />
          <div className="w-full">
            <ResolveDisputeButton onClick={handleRequestResolution} />
          </div>
        </div>
      </div>
    ) : (
      <div className="flex gap-3 px-6 py-6" data-testid={`${TEST_ID_BASE}-actions`}>
        <Button
          fullWidth
          label={<span className="normal-case font-medium">{t("common.cancel")}</span>}
          variant="text"
          onClick={handleClose}
          testId={`${TEST_ID_BASE}-cancel`}
        />
        <Button
          fullWidth
          label={
            <span className="normal-case font-medium">
              {t(`${LANG_BASE}.change-to-no-dispute`)}
            </span>
          }
          variant="outlined"
          onClick={onChangeToNoDispute}
          testId={`${TEST_ID_BASE}-change-to-no-dispute`}
        />
        <Button
          fullWidth
          label={
            <span className="normal-case font-medium">{t(`${LANG_BASE}.update-dispute`)}</span>
          }
          variant="contained"
          onClick={onUpdateDispute}
          testId={`${TEST_ID_BASE}-update-dispute`}
        />
      </div>
    );

  return (
    <div data-testid={`${TEST_ID_BASE}-drawer`} className="min-w-[400px]">
      <SideDrawer
        isOpen={isOpen}
        onClose={handleClose}
        onSubmit={handleClose}
        keepMounted={false}
        title={t(`${LANG_BASE}.${isNoDisputeRecord ? "no-dispute" : "dispute-title"}`)}
        submitLabel={t("common.ok")}
        customFooter={reviewFooter ?? raisedDisputeFooter}
        cancelLabel={t("common.cancel")}
      >
        <div className="flex flex-col w-[400px] p-6 max-h-full overflow-y-auto">
          {isLoading && <LoadingOverlay loading={isLoading} />}
          {!isLoading && isError && (
            <div className="text-sm text-red-600" data-testid={`${TEST_ID_BASE}-error`}>
              {t("common.something-went-wrong")}
            </div>
          )}
          {!isLoading && !isError && dispute && (
            <div className="mb-6 flex flex-col gap-1">
              <p className="text-sm font-bold">{t(`${LANG_BASE}.associated-movement`)}</p>
              <div className="text-sm" data-testid={`${TEST_ID_BASE}-associated-movement`}>
                {formatAssociatedMovement(dispute.associatedMovement)}
              </div>
            </div>
          )}
          {!isLoading &&
            !isError &&
            dispute &&
            historyEntries.map((historyEntry, historyIndex) => {
              const submittedAt = formatDateTime(historyEntry.createdAt).replace("\n", " ");
              // The record status represents only the current state. History entries must
              // keep their own declaration type so a previous Dispute is not displayed as
              // No Dispute after the record is changed.
              const fieldsLabel = getHistoryFieldsLabel(historyEntry.type);

              return (
                <div
                  key={`${historyEntry.createdAt}-${historyIndex}`}
                  className="flex flex-col gap-4"
                >
                  <div className="flex flex-col">
                    <p
                      className="text-sm font-bold"
                      data-testid={`${TEST_ID_BASE}-details-label-${historyEntry.type}-${historyIndex}`}
                    >
                      {t(`${LANG_BASE}.${fieldsLabel.details}`)}
                    </p>
                    <div
                      className="text-sm whitespace-pre-wrap wrap-break-word"
                      data-testid={`${TEST_ID_BASE}-details-${historyIndex}`}
                    >
                      {historyEntry.disputeDetails || "-"}
                    </div>
                  </div>
                  {historyEntry.documents.length > 0 && (
                    <div className="flex flex-col">
                      <p
                        className="text-sm font-bold"
                        data-testid={`${TEST_ID_BASE}-details-label-${historyEntry.type}-${historyIndex}`}
                      >
                        {t(`${LANG_BASE}.${fieldsLabel.documents}`)}
                      </p>
                      {historyEntry.documents.map((document) => (
                        <AttachmentsComponent
                          key={document.id ?? document.url}
                          documentName={document.originalFileName || "-"}
                          hideDocumentType
                          onDownload={() => {
                            handleDownload(
                              "document",
                              SERVICE,
                              document.url,
                              document.originalFileName,
                            );
                          }}
                          onPreview={() => {
                            handlePreview("document", SERVICE, document.url);
                          }}
                          langBase={LANG_BASE}
                        />
                      ))}
                    </div>
                  )}
                  <div className="flex flex-col gap-1 text-gray-500">
                    <div className="text-xs">
                      <span>{t(`${LANG_BASE}.grid.submitted-by`)}: </span>
                      <span data-testid={`${TEST_ID_BASE}-created-by-${historyIndex}`}>
                        {historyEntry.createdBy && (
                          <span className="text-gray-800">{historyEntry.createdBy}</span>
                        )}
                        {historyEntry.createdBy && historyEntry.createdByRole ? " / " : null}
                        {translateNormalizedRole(t, historyEntry.createdByRole)}
                      </span>
                    </div>
                    <div
                      className="text-xs"
                      data-testid={`${TEST_ID_BASE}-created-at-${historyIndex}`}
                    >
                      {submittedAt}
                    </div>
                  </div>
                  {historyEntries.length - 1 !== historyIndex &&
                    renderDivider(`${TEST_ID_BASE}-divider-${historyEntry.type}`)}
                </div>
              );
            })}
        </div>
      </SideDrawer>
    </div>
  );
};

export default DisputeDetailsDrawer;
